import { Box, Typography } from '@mui/material';
import { useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { changeWaiter } from '../../../api/order/changeWaiter';
import { Order } from '../../../apiTypes';
import EditModal from '../../../components/EditModal';
import FormSelect from '../../../components/forms/FormSelect';
import Image from '../../../components/Image';
import { t } from '../../../constants/translations';
import { useAdmin } from '../../../contexts/admin.context';
import { useCafe } from '../../../contexts/cafe.context';
import { useLocale } from '../../../contexts/locale.context';

type Props = {
  order: DbDocument<Order>;
  onClose: () => void;
};

const ChangeWaiterModal = ({ order, onClose }: Props) => {
  const { lang } = useLocale();
  const { workers, workersMap, worker } = useCafe();
  const { cafeSlug, tablesMap, tablesByIds } = useAdmin();
  const form = useForm<Order>({ defaultValues: order });

  const waiters = useMemo(() => workers.filter((w) => w.credentials.pos_terminal).map((w) => w.id), [workers]);

  const tables = useMemo(() => tablesMap.tables.map((t) => t.id), [tablesMap.tables]);

  const handleSubmit = useCallback(
    (values: Order) => {
      const table = tablesMap.tables.find((t) => t.id === values.tableId);
      const waiter = workersMap[values.workerId];
      if (waiter && worker) {
        changeWaiter({ order, cafeSlug, waiter, worker, table });
        onClose();
      }
    },
    [cafeSlug, onClose, order, tablesMap.tables, worker, workersMap]
  );

  return (
    <EditModal<Order>
      form={form}
      open
      title={t.settings[lang]}
      onClose={onClose}
      submitButton={t.save_changes[lang]}
      onSubmit={handleSubmit}
      size="xs"
    >
      <FormSelect
        name="workerId"
        label={t.waiter[lang]}
        options={waiters}
        renderOption={(id) => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Image src={workersMap[id]?.photoUrl} rounded />
            <Typography>{workersMap[id]?.name}</Typography>
          </Box>
        )}
      />

      {tablesMap.enabled && (
        <FormSelect
          name="tableId"
          label={t.table[lang]}
          options={tables}
          renderOption={(id) => <Typography>{tablesByIds[id]?.name}</Typography>}
          sx={{ mt: 3 }}
        />
      )}
    </EditModal>
  );
};

export default ChangeWaiterModal;
